"use client";

import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { logjs } from "@lib/client";
import s from "./styles";
import Image from "next/image";

import { providers } from "@lib/client";
import { usePhotoDialog } from "./usePhotoDialog";
const { useWebAppContext } = providers;

const log = new logjs("PhotoDialog");

function PhotoDialog({ open, onClose, params }) {
  log.debug("PhotoDialog", { open, params });

  const { state, actions } = useWebAppContext();
  const [formData, formProps] = usePhotoDialog(state, actions, params);
  const { mitsumoriNo, photoData, images, error, IsLoading } = formData;
  const { isEditMode } = params || false;
  const { PartsName, index } = params;
  log.debug(`FileupDialog isEditMode:${isEditMode}`, formData);

  // ドラッグ&ドロップ用の状態
  const [dragOverIndex, setDragOverIndex] = useState(-1);

  // ファイル入力用の参照
  const fileInputsRef = useRef({});
  const bulkFileInputRef = useRef(null);
  const [previewPict, setPreviewPict] = useState(null);

  useEffect(() => {
    if (!open) return;
    formProps.initParts();
  }, [open]);

  // ファイル変更ハンドラ（単一）
  const onChangeFiles = async (seq, fileList) => {
    log.debug("onChangeFiles START", { seq, fileList: fileList?.length });
    if (!fileList || fileList.length === 0) return;
    if (fileList.length > 1) {
      actions.showAlert(`画像ファイルは1枚まで登録できます。`);
      return;
    }
    const file = fileList[0];
    const okType = /image\/(jpeg|png)/i.test(file.type);
    if (!okType) {
      actions.showAlert("JPEGまたはPNGファイルを指定してください。");
      return;
    }
    if (file.size > 4000000) {
      actions.showAlert("アップロードできるファイルサイズは、４Ｍバイトまでとなります");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = String(e.target.result);
      formProps.setImages((prev) => prev.map((p) => (p.seq === seq ? { ...p, url: dataUrl, canDelete: true, originalname: file.name } : p)));
      // data URL + 元ファイル名をサーバへ渡す
      const payload = [{ originalname: file.name, url: dataUrl }];
      await formProps.uploadPhoto(seq, payload);
    };
    reader.readAsDataURL(file);
  };

  // ファイル変更ハンドラ（一括）
  const onChangeFilesBulk = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList).filter((file) => {
      const okType = /image\/(jpeg|png)/i.test(file.type);
      if (!okType) {
        actions.showAlert("JPEGまたはPNGファイルを指定してください。");
        return false;
      }
      if (file.size > 4000000) {
        actions.showAlert("アップロードできるファイルサイズは、４Ｍバイトまでとなります");
        return false;
      }
      return true;
    });

    const useFiles = files.slice(0, 10);

    try {
      log.debug("Bulk upload: deleteAllImages START");
      await formProps.deleteAllImages();
      log.debug("Bulk upload: deleteAllImages END");

      const newImages = [];

      for (let i = 0; i < useFiles.length; i++) {
        const file = useFiles[i];

        log.debug(`Bulk upload: reading file ${file.name}`);
        const url = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });

        newImages.push({
          seq: i + 1,
          url: url,
          canDelete: true,
          originalname: file.name,
        });
        log.debug(`Bulk upload: file ${file.name} loaded`);
      }

      log.debug("Bulk upload: filling empty slots");
      while (newImages.length < 10) {
        newImages.push({
          seq: newImages.length + 1,
          url: "/images/noimage.png",
          canDelete: false,
          originalname: "",
        });
      }

      log.debug("Bulk upload: updating UI");
      formProps.setImages(newImages);

      log.debug("Bulk upload: uploadPhoto START");
      await formProps.uploadPhoto(99, newImages);
      log.debug("Bulk upload: uploadPhoto END");

    } catch (error) {
      log.error("onChangeFilesBulk error:", error);
    }

    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
    log.debug("Bulk upload: initParts START");
    formProps.initParts();
    log.debug("Bulk upload: initParts END");
  };

  // ドラッグ&ドロップハンドラ
  const handleDrop = (seq, e) => {
    e.preventDefault();
    setDragOverIndex(-1);
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    onChangeFiles(seq, files);
  };

  // 削除ハンドラ
  const handleDelete = async (seq) => {
    await formProps.deletePhoto(seq);
  };

  // ファイル選択トリガー
  const triggerPick = (seq) => {
    const input = fileInputsRef.current[seq];
    if (input) input.click();
  };

  // 一括ファイル選択トリガー
  const triggerBulkPick = async () => {
    // 登録済みの画像があるか確認
    const hasExisting = images.some((p) => p.canDelete);

    // 登録済みがない場合は即クリック
    if (!hasExisting) {
      if (bulkFileInputRef.current) bulkFileInputRef.current.click();
      return;
    }
    
    const _ret = await actions.showConfirm("現在登録されている写真に上書きされますがよろしいですか？");
    if (!_ret) return;
    if (bulkFileInputRef.current) bulkFileInputRef.current.click();
  };

  // 備考更新ハンドラ
  const updateBikou = async () => {
    await formProps.updateBikou();
  };

  // 写真グリッドのレンダリング
  const renderPhotoGrid = () => {
    const maxSlots = 10;
    const photosPerRow = 5;
    const rows = Math.ceil(maxSlots / photosPerRow);

    return Array.from({ length: rows }).map((_, rowIdx) => (
      <s.PhotoGridRow key={rowIdx}>
        {Array.from({ length: photosPerRow }).map((__, colIdx) => {
          const seq = rowIdx * photosPerRow + colIdx + 1;
          const slot = images[seq - 1];
          log.debug("****slot++++",slot);
          log.debug("****previewPict++++",previewPict);
          if (seq > maxSlots) return <td key={colIdx} />;

          return (
            <s.PhotoCell key={colIdx}>
              <s.PartsMini>
                <s.PhotoDrop
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOverIndex(seq);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    // 子要素への移動時はdragLeaveを発火させない
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setDragOverIndex(-1);
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => handleDrop(seq, e)}
                  className={dragOverIndex === seq ? "drag-over" : ""}
                >
                  <img
                    src={slot?.url || "/images/noimage.png"}
                    alt="写真"
                    style={{ cursor: slot?.url ? "pointer" : "default" }}
                    onClick={() => {
                      if (slot?.url && slot.url !== "/images/noimage.png") {
                        setPreviewPict({ url: slot.url });
                      }
                    }}
                  />
                </s.PhotoDrop>
                {isEditMode && (
                  <s.MiniRightBox>
                    <s.FileMask2>
                      <s.HiddenFile
                        ref={(el) => {
                          if (!el) return;
                          fileInputsRef.current[String(seq)] = el;
                        }}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => onChangeFiles(seq, e.target.files)}
                      />
                      <s.FileMask2Span
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (slot?.canDelete) {
                            const _ret = await params.actions?.showConfirm?.("確認", "現在登録されている写真に上書きされますがよろしいですか？");
                            if (!_ret) return;
                          }
                          triggerPick(seq);
                        }}
                      >
                        参照
                      </s.FileMask2Span>
                      <s.HiddenText type="text" />
                    </s.FileMask2>
                    {slot?.canDelete ? (
                      <s.BtnGray onClick={() => handleDelete(seq)} className="delete_btn">
                        削除
                      </s.BtnGray>
                    ) : (
                      <s.BtnGray className="delete_btn disable-btn">削除</s.BtnGray>
                    )}
                  </s.MiniRightBox>
                )}
              </s.PartsMini>
            </s.PhotoCell>
          );
        })}
      </s.PhotoGridRow>
    ));
  };

  const { request } = photoData || {};

  const partsName = photoData?.partsName?.P_PartsName;
  const othPartsName = photoData?.PartsAnsers?.OthPartsName;

  log.debug("photoData", photoData);
  const isModal = false;
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        hideBackdrop={!isModal}
        style={!isModal ? { pointerEvents: "none" } : { pointerEvents: "auto" }}
        BackdropProps={
          !isModal
            ? {
                style: { pointerEvents: "none" }, // 背景は操作不可にする
              }
            : {}
        }
        PaperProps={
          !isModal
            ? {
                style: { pointerEvents: "auto" }, // ダイアログ自体は操作可能にする
                sx: {
                  maxHeight: "none",
                  height: "auto",
                  overflow: "visible",
                },
              }
            : {}
        }
      >
        <s.PhotoContainer>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "url(/images/bg.png) 0 0",
            }}
          >
            <s.FlexRow>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  background: "url(/images/window_title.png) no-repeat 0 50%",
                  padding: "5px 0 0 35px",
                  color: "#333",
                  fontSize: "24px",
                  lineHeight: "30px",
                  marginBottom: "10px",
                }}
              >
                {isEditMode ? "写真登録" : "写真参照"}
              </Typography>
            </s.FlexRow>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                backgroundColor: "#999",
                color: "#fff",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#777",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: 3,
              background: "url(/images/bg.png) 0 0",
              overflow: "visible !important",
            }}
          >
            {/* 車輌情報 */}
            <s.DetailTableCustom>
              <tbody>
                <tr>
                  <td>
                    <span className="label">見積番号</span>
                    <span className="value">{request?.MitsumoriNo || ""}</span>

                    <span className="space" />

                    <span className="label">車名</span>
                    <span className="value">{request?.CarName || ""}</span>

                    <span className="space" />

                    <span className="label">品名</span>
                    <span className="value">{othPartsName || partsName || PartsName || ""}</span>
                  </td>
                </tr>

                <tr>
                  <td>
                    <span className="label">車輌型式</span>
                    <span className="value">{request?.Sharyo_Katashiki || ""}</span>

                    <span className="space" />

                    <span className="label">年式</span>
                    <span className="value">{request?.Sharyo_Nenshiki || ""}</span>
                  </td>
                </tr>
              </tbody>
            </s.DetailTableCustom>

            {/* 一括アップロード */}
            {isEditMode && (
            <s.NormalTable className="imgedit" style={{ marginBottom: 5 }}>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #333", padding: "8px 2px" }}>
                    <s.MiniRightBox>
                      写真をまとめて登録する
                      <s.FileMask2>
                        <s.HiddenFile ref={bulkFileInputRef} multiple accept="image/jpeg,image/png" type="file" onChange={(e) => onChangeFilesBulk(e.target.files)} />
                        <s.FileMask2Span onClick={triggerBulkPick}>参照</s.FileMask2Span>
                        <s.HiddenText type="text" />
                      </s.FileMask2>
                    </s.MiniRightBox>
                  </td>
                </tr>
              </tbody>
            </s.NormalTable>
            )}

            {/* エラーメッセージ */}
            {error && <s.ErrorMessage>{error}</s.ErrorMessage>}

            {/* 写真グリッド */}
            <s.NormalTable className="imgedit" style={{ marginBottom: 5 }}>
              <s.Caption>損傷箇所の写真を添付してください</s.Caption>
              <tbody>{renderPhotoGrid()}</tbody>
            </s.NormalTable>

            {/* 備考 */}
            {/*
            <s.TextNoteTable className="normal">
              <s.Caption>備考等、特記事項がありましたら入力して下さい</s.Caption>
              <tbody>
                <tr>
                  <td className="note" style={{ width: "100%", textAlign: "center" }}>
                    {formProps.input_bikou?.({
                      multiline: true,
                      maxRows: 10,
                      rows: 4,
                      style: { width: "98%" },
                    })}
                  </td>
                </tr>
              </tbody>
            </s.TextNoteTable>
            */}

            {/* 操作ボタン */}
            {isEditMode && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <s.BtnRed onClick={onClose} style={{ marginLeft: "10px" }}>
                  ▶▶画像を登録する
                </s.BtnRed>
              </Box>
            )}

            {/* ローディング表示 */}
            {IsLoading && (
              <s.LoadingOverlay>
                <s.LoadingSpinner />
              </s.LoadingOverlay>
            )}
          </DialogContent>
        </s.PhotoContainer>
      </Dialog>

      {/* 写真モーダル */}
      <Modal open={!!previewPict} onClose={() => setPreviewPict(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            height: 400,
            bgcolor: "#fff",
            borderRadius: 1,
            boxShadow: 24,
            p: 1,
            outline: "none",
          }}
        >
          {/* × ボタン */}
          <Box
            onClick={() => setPreviewPict(null)}
            sx={{
              position: "absolute",
              top: -12,
              right: -12,
              zIndex: 10,
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#000",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 20,
              fontWeight: "bold",
              border: "2px solid #fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
            }}
          >
            ×
          </Box>

          {/* 拡大画像 */}
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Image
              src={previewPict?.url || ""}
              alt=""
              fill
              sizes="400px"
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default PhotoDialog;
